<template>
  <div
    class="min-h-screen flex items-center justify-center p-4 sm:p-6 relative overflow-hidden bg-slate-50 dark:bg-[#0B1120] text-slate-900 dark:text-white"
  >
    <!-- Ambient Background Mesh/Glow -->
    <div class="fixed inset-0 pointer-events-none overflow-hidden">
      <div
        class="absolute -top-32 -left-32 w-80 h-80 sm:w-96 sm:h-96 bg-indigo-500/10 dark:bg-indigo-600/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-32 -right-32 w-80 h-80 sm:w-96 sm:h-96 bg-purple-500/10 dark:bg-purple-600/10 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="w-full max-w-md relative z-10">
      <!-- Loading State -->
      <transition
        name="fade-slide"
        appear
      >
        <div
          v-if="loading"
          class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-center border border-slate-200/90 dark:border-[#263449] shadow-2xl max-w-sm mx-auto"
        >
          <AppLoader
            variant="card"
            size="lg"
            message="Processing Attendance"
            subtext="Verifying NFC card token with school database"
            color="indigo"
            icon="cap"
          />
        </div>
      </transition>

      <!-- Success State -->
      <transition
        name="success-bounce"
        appear
      >
        <div
          v-if="success"
          class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-center border border-slate-200/90 dark:border-[#263449] shadow-2xl relative overflow-hidden"
        >
          <!-- Confetti Animation -->
          <div class="absolute inset-0 pointer-events-none">
            <div
              v-for="i in 30"
              :key="i"
              class="absolute w-2 h-2 rounded-full animate-confetti"
              :style="{
                left: Math.random() * 100 + '%',
                backgroundColor: ['#10B981', '#059669', '#34D399', '#6EE7B7'][
                  Math.floor(Math.random() * 4)
                ],
                animationDelay: Math.random() * 2 + 's',
                animationDuration: 2 + Math.random() * 2 + 's',
                transform: `translateY(-100vh) rotate(${
                  Math.random() * 360
                }deg)`,
              }"
            ></div>
          </div>
          <div class="flex flex-col items-center space-y-6 relative z-10">
            <!-- Success Icon with Pulse -->
            <div class="relative">
              <div
                class="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-tr from-emerald-500 to-teal-400 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-lg shadow-emerald-500/20"
              >
                <CheckCircle class="w-10 h-10 sm:w-12 sm:h-12 text-white animate-check-draw" />
              </div>
              <div
                class="absolute inset-0 bg-emerald-400 rounded-2xl sm:rounded-3xl animate-ping opacity-25"
              ></div>
            </div>

            <div class="space-y-3 w-full">
              <span
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-800/40"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Attendance Verified
              </span>

              <h2
                class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight"
              >
                Attendance Confirmed
              </h2>

              <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                Successfully recorded presence for:
              </p>

              <div
                class="bg-slate-50 dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] rounded-2xl p-4 shadow-xs"
              >
                <p class="font-bold text-slate-900 dark:text-white text-lg sm:text-xl">
                  {{ studentName }}
                </p>
              </div>
            </div>

            <div
              class="text-xs text-slate-400 dark:text-slate-500 flex items-center justify-center gap-1.5"
            >
              <Clock class="w-4 h-4 text-slate-400" />
              <span>{{ currentTime }}</span>
            </div>
          </div>
        </div>
      </transition>

      <!-- Error State -->
      <transition
        name="shake"
        appear
      >
        <div
          v-if="error"
          class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-center border border-slate-200/90 dark:border-[#263449] shadow-2xl"
        >
          <div class="flex flex-col items-center space-y-6">
            <!-- Error Icon with Shake -->
            <div class="relative">
              <div
                class="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-tr from-rose-500 to-pink-500 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-lg shadow-rose-500/20"
              >
                <AlertCircle class="w-10 h-10 sm:w-12 sm:h-12 text-white animate-shake" />
              </div>
              <div
                class="absolute inset-0 bg-rose-400 rounded-2xl sm:rounded-3xl animate-ping opacity-25"
              ></div>
            </div>

            <div class="space-y-3 w-full">
              <h2
                class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight"
              >
                Attendance Failed
              </h2>

              <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                We encountered an issue while verifying records
              </p>

              <div
                class="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 rounded-2xl p-4 shadow-xs"
              >
                <p class="text-xs sm:text-sm font-medium text-rose-700 dark:text-rose-300">
                  {{ error }}
                </p>
              </div>
            </div>

            <button
              @click="retryAttendance"
              class="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-md shadow-indigo-600/20 active:scale-98"
            >
              <RefreshCw class="w-4 h-4" />
              <span class="text-sm">Try Again</span>
            </button>
          </div>
        </div>
      </transition>

      <!-- Footer Badge -->
      <transition
        name="fade-up"
        appear
      >
        <div class="mt-6 text-center">
          <div
            class="inline-flex items-center gap-2 bg-white/80 dark:bg-[#18243A]/80 backdrop-blur-md rounded-full px-4 py-2 border border-slate-200/80 dark:border-[#263449] shadow-xs text-xs text-slate-600 dark:text-slate-400 font-medium"
          >
            <div class="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
            <span>Powered by Educanium Smart NFC</span>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
  import { onMounted, ref, computed } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useStore } from "vuex";
  import {
    CheckCircle,
    AlertCircle,
    Clock,
    RefreshCw,
  } from "lucide-vue-next";
  import axios from "axios";
  const BASE_URL = process.env.VUE_APP_BASE_URL;
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const studentId = route.params.studentId;
  const loading = ref(true);
  const success = ref(false);
  const error = ref("");
  const studentName = ref("Student");
  const currentTime = computed(() => {
    return new Date().toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  });
  const markAttendance = async () => {
    try {
      const token = localStorage.getItem("token");
      loading.value = true;
      success.value = false;
      error.value = "";
      // ✅ Get student name
      const res = await axios.get(`${BASE_URL}/api/students/${studentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      studentName.value = res.data.name;
      // ✅ Dispatch markAttendance action from store
      await store.dispatch("markAttendance", {
        studentId,
        status: "present",
        subject: "",
        notes: "Marked via NFC link",
        attendanceByNFC: false,
      });
      success.value = true;

      setTimeout(() => {
        router.replace("/");
      }, 5000);
    } catch (err) {
      error.value =
        err.response?.data?.error ||
        "Failed to mark attendance. Please try again.";
      console.error("MarkAttendance Error:", err);
    } finally {
      loading.value = false;
    }
  };
  const retryAttendance = () => {
    markAttendance();
  };
  onMounted(() => {
    markAttendance();
  });
</script>

<style scoped>
  /* Blob Animation */
  @keyframes blob {
    0% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(40px, -60px) scale(1.15);
    }
    66% {
      transform: translate(-30px, 30px) scale(0.9);
    }
    100% {
      transform: translate(0px, 0px) scale(1);
    }
  }
  .animate-blob {
    animation: blob 8s infinite cubic-bezier(0.42, 0, 0.58, 1);
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }

  /* Custom Animations */
  @keyframes spin-slow {
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes scale-in {
    0% {
      transform: scale(0) rotate(180deg);
      opacity: 0;
    }
    50% {
      transform: scale(1.1) rotate(90deg);
      opacity: 0.8;
    }
    100% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
    }
  }
  @keyframes fade-in-up {
    0% {
      transform: translateY(20px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes check-draw {
    0% {
      stroke-dasharray: 0 100;
    }
    100% {
      stroke-dasharray: 100 0;
    }
  }
  @keyframes confetti {
    0% {
      transform: translateY(-100vh) rotate(0deg) scale(0.5);
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    100% {
      transform: translateY(100vh) rotate(720deg) scale(1.2);
      opacity: 0;
    }
  }
  @keyframes tick {
    0%,
    50% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(-10deg);
    }
    75% {
      transform: rotate(10deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }
  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-8px);
    }
    75% {
      transform: translateX(8px);
    }
  }

  /* Animation Classes */
  .animate-spin-slow {
    animation: spin-slow 3s linear infinite;
  }
  .animate-scale-in {
    animation: scale-in 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  .animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out;
  }
  .animate-check-draw {
    animation: check-draw 0.5s ease-in-out 0.3s both;
  }
  .animate-confetti {
    animation: confetti linear;
  }
  .animate-tick {
    animation: tick 2s ease-in-out infinite;
  }
  .animate-shake {
    animation: shake 0.5s ease-in-out;
  }

  /* Animation Delays */
  .animation-delay-200 {
    animation-delay: 0.2s;
  }
  .animation-delay-400 {
    animation-delay: 0.4s;
  }
  .animation-delay-600 {
    animation-delay: 0.6s;
  }
  .animation-delay-800 {
    animation-delay: 0.8s;
  }

  /* Vue Transitions */
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
  .fade-slide-enter-from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }
  .success-bounce-enter-active {
    transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  .success-bounce-enter-from {
    opacity: 0;
    transform: translateY(50px) scale(0.8);
  }
  .shake-enter-active {
    transition: all 0.5s ease-out;
  }
  .shake-enter-from {
    opacity: 0;
    transform: translateX(-20px) scale(0.9);
  }
  .fade-up-enter-active {
    transition: all 0.8s ease-out;
  }
  .fade-up-enter-from {
    opacity: 0;
    transform: translateY(20px);
  }

  /* Glassmorphism Effect */
  .bg-white\/85 {
    background-color: rgba(255, 255, 255, 0.85);
  }
  .bg-white\/70 {
    background-color: rgba(255, 255, 255, 0.7);
  }
  .border-white\/30 {
    border-color: rgba(255, 255, 255, 0.3);
  }
  .border-white\/40 {
    border-color: rgba(255, 255, 255, 0.4);
  }
</style>
